import React, { useEffect, useState, useRef } from 'react';
import Chart from 'chart.js/auto';
import annotationPlugin from 'chartjs-plugin-annotation';
import formatNumber from "../../helpers/NumberFormat";
import "./HomeClientNew.scss";

Chart.register(annotationPlugin);

export default function TimelineChart({ main, isMobile }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const today = new Date();
  const formattedDate = today.toLocaleDateString("en-GB");
  const transactions = main?.user?.transactions || [];
  const [selectedRange, setSelectedRange] = useState("0");

  const ranges = [
    { label: "30 DAYS", value: "30" },
    { label: "90 DAYS", value: "90" },
    { label: "180 DAYS", value: "180" },
  ];

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.destroy();
    }

    const labels = [];
    const data = [];
    const annotations = {};
    const balanceNow = parseFloat(main?.user?.balance ?? 0);
    const days = parseInt(selectedRange);
    const hasRange = days > 0;

    const today = new Date();
    today.setHours(23, 59, 59, 999);

    const rangeStartDate = hasRange ? new Date(today.getTime() - days * 86400000) : null;
    if (rangeStartDate) {
      rangeStartDate.setHours(0, 0, 0, 0);
    }

    const incomingTxs = (transactions ?? [])
      .filter(tx => {
        const isReceivedIn = tx.type === 'in' && tx.status?.name === 'Received';
        const txDate = new Date(tx.execution_date);
        return isReceivedIn && (!rangeStartDate || (txDate >= rangeStartDate && txDate <= today));
      })
      .map(tx => ({
        amount: parseFloat(tx.amount),
        date: new Date(tx.execution_date)
      }))
      .sort((a, b) => a.date - b.date);

    if (incomingTxs.length === 0) {
      const today = new Date();
      const start = new Date(today);
      start.setDate(today.getDate() - 7);
      const mid = new Date((start.getTime() + today.getTime()) / 2);

      labels.push(
        start.toLocaleDateString("en-GB", { day: '2-digit', month: 'short' }),
        mid.toLocaleDateString("en-GB", { day: '2-digit', month: 'short' }),
        today.toLocaleDateString("en-GB", { day: '2-digit', month: 'short' })
      );
      data.push(0, 0, 0);
    } else {
      const firstTxDate = incomingTxs[0].date;
      const startDate = new Date(firstTxDate);
      startDate.setDate(startDate.getDate() - 7);

      labels.push(startDate.toLocaleDateString("en-GB", { day: '2-digit', month: 'short' }));

      let cumulative = 0;

      if (rangeStartDate) {
        const previousTxs = (transactions ?? [])
          .filter(tx => {
            const isReceivedIn = tx.type === 'in' && tx.status?.name === 'Received';
            const txDate = new Date(tx.execution_date);
            return isReceivedIn && txDate < rangeStartDate;
          })
          .map(tx => parseFloat(tx.amount));

        cumulative = previousTxs.reduce((sum, amount) => sum + amount, 0);
      }

      data.push(cumulative);
      const txLabels = [];

      incomingTxs.forEach(tx => {
        cumulative += tx.amount;
        const label = tx.date.toLocaleDateString("en-GB", { day: '2-digit', month: 'short' });
        labels.push(label);
        data.push(cumulative);
        txLabels.push(label);
      });

      const lastTxDate = incomingTxs[incomingTxs.length - 1].date;
      const today = new Date();
      const midDate = new Date((lastTxDate.getTime() + today.getTime()) / 2);

      labels.push(midDate.toLocaleDateString("en-GB", { day: '2-digit', month: 'short' }));
      data.push(balanceNow);

      //this is only an extra date to extend the line 
      // const todayLabel = today.toLocaleDateString("en-GB", { day: '2-digit', month: 'short' });
      // if (!labels.includes(todayLabel)) {
      //   labels.push(todayLabel);
      //   data.push(balanceNow);
      // }

      for (let i = 0; i < txLabels.length; i++) {
        if (i % 2 === 0) {
          const xMin = txLabels[i];
          const xMax = txLabels[i + 1] ?? xMin;
          annotations[`box${i}`] = {
            type: 'box',
            xMin,
            xMax,
            backgroundColor: 'rgba(255, 255, 255, 0.07)',
            borderWidth: 0
          };
        }
      }

      incomingTxs.forEach((tx, index) => {
        const txLabel = tx.date.toLocaleDateString("en-GB", { day: '2-digit', month: 'short' });
        annotations[`tx${index}`] = {
          type: 'line',
          mode: 'vertical',
          scaleID: 'x',
          value: txLabel,
          borderColor: 'rgba(0, 123, 255, 0.05)',
          borderWidth: 1,
          label: {
            enabled: true,
            content: `+${tx.amount} €`,
            position: 'top'
          }
        };
      });

    }

    chartRef.current = new Chart(canvasRef.current, {
      type: 'line',
      data: {
        labels,
        datasets: [{
          label: ' Balance',
          data,
          borderColor: '#ececec',
          backgroundColor: '#c9c9c930',
          fill: true,
          tension: 0.3
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          annotation: { annotations }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              color: '#ffffff',
              stepSize: 1000
            },
            grid: {
              color: 'rgba(161, 161, 161, 0.3)'
            }
          },
          x: {
            ticks: {
              color: '#ffffff'
            },
            grid: {
              color: 'rgba(161, 161, 161, 0.3)'
            }
          }
        }
      }
    });

    return () => {
      chartRef.current?.destroy();
    };
  }, [main, selectedRange]);

  return (
    <div className='gradient-background' style={isMobile ? { height: '680px' } : {}}>
      <div className="card-section mt-2" style={{ height: "100px" }}>
        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start w-100 px-2">
          <div className="border border-new mb-3 mb-sm-0">
            <h5 className="text-muted-custom">
              {main?.user?.first_name} {main?.user?.last_name}
            </h5>
            <div className="text-muted-custom">
              Current Account:
              <span className="account-number">
                {' '}{main?.user?.detail?.account_number?.replace(/^.*?-/, '')}
              </span>
            </div>
          </div>

          {
            !isMobile && (
              <div className="day-selector d-flex gap-3">
                {ranges.map(({ label, value }) => (
                  <div
                    key={value}
                    className={`selector-option ${selectedRange === value ? "active" : ""}`}
                    onClick={() => setSelectedRange(value)}
                  >
                    {label}
                  </div>
                ))}
              </div>
            )
          }
        </div>

      </div>
      <div className="card-section" style={{ height: '250px' }}>
        {!isMobile ? (
          <div className='card-balance-div mb-4'>
            <div>
              Balance as of {formattedDate}
              <div className="card-balance account-number" style={{ display: 'inline' }}>
                {formatNumber(main?.user?.balance)} €
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ marginRight: '20px' }}>
                Revenue:
                <div className="card-balance account-number" style={{ display: 'inline' }}>
                  {formatNumber(main?.incoming)} €
                </div>
              </div>
              <div>
                Outputs:
                <div className="card-balance account-number" style={{ display: 'inline' }}>
                  {formatNumber(main?.outgoing)} €
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div
              className="card-balance-div mb-4"
              style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'flex-start' : 'center',
                gap: '20px',
              }}
            >
              <div className='mt-3'>
                Balance as of {formattedDate}:<br />
                <span className="card-balance account-number">
                  {formatNumber(main?.user?.balance)} €
                </span>
              </div>

              <div>
                Revenue:<br />
                <span className="card-balance account-number">
                  {formatNumber(main?.incoming)} €
                </span>
              </div>

              <div>
                Outputs:<br />
                <span className="card-balance account-number">
                  {formatNumber(main?.outgoing)} €
                </span>
              </div>
            </div>

            <div className="w-100 m-auto day-selector d-flex justify-content-between mb-2">
              {ranges.map(({ label, value }) => (
                <div
                  key={value}
                  className={`selector-option ${selectedRange === value ? "active" : ""}`}
                  onClick={() => setSelectedRange(value)}
                >
                  {label}
                </div>
              ))}
            </div>
          </>
        )}
        <canvas ref={canvasRef}></canvas>
      </div>
    </div>
  );
}